/**
 * PhotoDiary — daily photo journal for a school garden.
 *
 * Renders under the snapshot card on `/schulgarten` once a polygon
 * exists. Lets the class upload photos (≤ 8 MB, server downscales to
 * 1080 px JPEG), add a one-line caption + optional date, and review
 * the timeline in newest-first order.
 *
 * Privacy: photos carry only the admin-set `school_label` plus the
 * already-rounded centroid (≈ 11 km granularity). Real names are never
 * captured by the upload form. The image bytes are streamed from
 * MongoDB via `/api/school-garden/photos/{id}.jpg`.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera, CalendarBlank, Trash, Sparkle, Image as ImageIcon, ImagesSquare,
} from "@phosphor-icons/react";
import {
  uploadSchoolGardenPhoto, fetchMySchoolGardenPhotos,
  deleteSchoolGardenPhoto, schoolGardenPhotoUrl,
} from "@/lib/api";

const MAX_BYTES = 8 * 1024 * 1024; // matches backend MAX_BYTES_IN

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDateDe(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("de-DE", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PhotoDiary() {
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [photoDate, setPhotoDate] = useState(todayIso());
  const [preview, setPreview] = useState(null);

  const reload = async () => {
    try {
      const r = await fetchMySchoolGardenPhotos();
      setPhotos(r.photos || []);
    } catch {
      // silent fail — backend may be cold
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("Datei zu groß (max. 8 MB).");
      e.target.value = "";
      return;
    }
    handleUpload(f);
    e.target.value = "";
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const r = await uploadSchoolGardenPhoto(file, { caption, photo_date: photoDate });
      if (r.ok) {
        toast.success("Foto gespeichert ✓");
        setCaption("");
        setPhotoDate(todayIso());
        await reload();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Hochladen fehlgeschlagen.";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Dieses Foto wirklich löschen?")) return;
    try {
      await deleteSchoolGardenPhoto(id);
      toast.success("Foto gelöscht");
      await reload();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  return (
    <section className="eid-card p-5 space-y-4" data-testid="school-photo-diary">
      <header className="flex items-center gap-2">
        <ImagesSquare size={20} weight="fill" className="text-[#00FA9A]" />
        <h2 className="text-lg font-display font-bold tracking-tight">Tages-Foto-Tagebuch</h2>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {photos.length} / 200
        </span>
      </header>

      <p className="text-xs text-zinc-400 leading-relaxed">
        Knipst jeden Tag ein Foto vom Garten — von einer Blüte, einem Käfer,
        dem Wetter. Über das Jahr seht ihr, wie sich euer Stück Erde verändert.
      </p>

      {/* Upload card */}
      <div className="border border-dashed border-[#00FA9A]/40 bg-[#0A0A0A] p-4 rounded-sm space-y-3" data-testid="diary-upload-box">
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">
            <span className="flex items-center gap-1.5">
              <CalendarBlank size={12} /> Datum
            </span>
            <input
              type="date"
              value={photoDate}
              max={todayIso()}
              onChange={(e) => setPhotoDate(e.target.value)}
              className="mt-1 w-full bg-[#121212] border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono"
              data-testid="diary-date"
            />
          </label>
          <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">
            <span className="flex items-center gap-1.5">
              <Sparkle size={12} /> Bildunterschrift (optional)
            </span>
            <input
              type="text"
              maxLength={280}
              placeholder='z. B. "Heute hat die Sonnenblume Knospen gemacht."'
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="mt-1 w-full bg-[#121212] border border-white/10 rounded-sm px-2 py-1.5 text-sm"
              data-testid="diary-caption"
            />
          </label>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          onChange={onFile}
          className="hidden"
          data-testid="diary-file-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#00FA9A] text-black px-4 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-[#33FBAE] disabled:opacity-50 rounded-sm"
          data-testid="diary-upload-btn"
        >
          <Camera size={16} weight="fill" />
          {uploading ? "Wird hochgeladen …" : "Foto hochladen"}
        </button>
        <p className="text-[10px] font-mono text-zinc-500">
          Max. 8 MB · wird auf 1080 px verkleinert · EXIF (Ort, Gerät) wird automatisch entfernt
        </p>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-sm text-zinc-500">Lade Tagebuch …</div>
      ) : photos.length === 0 ? (
        <div className="text-center py-8 text-sm text-zinc-400" data-testid="diary-empty">
          <ImageIcon size={28} className="mx-auto mb-2 text-[#00FA9A]/40" />
          Noch keine Fotos. Macht das erste Bild — heute ist ein guter Tag dafür.
        </div>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="diary-grid">
          <AnimatePresence initial={false}>
            {photos.map((p) => (
              <motion.li
                key={p.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.25 }}
                className="group relative border border-white/10 bg-[#121212] rounded-sm overflow-hidden"
                data-testid={`diary-photo-${p.id}`}
              >
                <button
                  type="button"
                  onClick={() => setPreview(p)}
                  className="block aspect-square w-full overflow-hidden bg-white/5"
                >
                  <img
                    src={schoolGardenPhotoUrl(p.id)}
                    alt={p.caption || "Schulgarten"}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </button>
                <div className="p-2.5 space-y-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FA9A]">
                    {fmtDateDe(p.photo_date)}
                  </div>
                  {p.caption && (
                    <p className="text-xs text-white leading-snug line-clamp-2">
                      {p.caption}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(p.id)}
                  className="absolute top-1.5 right-1.5 bg-black/55 hover:bg-[#FF6A1F] text-white p-1.5 rounded-sm opacity-0 group-hover:opacity-100 transition"
                  aria-label="Foto löschen"
                  data-testid={`diary-delete-${p.id}`}
                >
                  <Trash size={12} weight="bold" />
                </button>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {preview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPreview(null)}
            data-testid="diary-lightbox"
          >
            <motion.div
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.92 }}
              className="max-w-3xl w-full bg-[#121212] rounded-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={schoolGardenPhotoUrl(preview.id)}
                alt={preview.caption || "Schulgarten"}
                className="w-full max-h-[70vh] object-contain bg-white/5"
              />
              <div className="p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FA9A]">
                  {fmtDateDe(preview.photo_date)}
                </div>
                {preview.caption && (
                  <p className="text-sm text-white mt-1.5">{preview.caption}</p>
                )}
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  className="mt-4 text-[11px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white"
                >
                  Schließen
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
